import { useCallback, useEffect, useState } from "react";
import { useFormContext } from "react-hook-form";
import type {
  ShowEndpointQueryParams,
  ShowResponseEndpoint,
} from "@circle-react/api/paywallCheckoutApi.types";
import { usePaywallCheckoutContext } from "@circle-react/contexts/Paywalls/paywallCheckoutContext";
import { classNames } from "@circle-react/helpers/twMergeWithCN";
import { getQueryParam } from "@circle-react/helpers/urlHelpers";
import type { CheckoutFormValuesProps } from "../../../types/CheckoutFormValuesProps";
import { CouponBadge } from "./CouponBadge";
import { CouponField } from "./CouponField";

interface CouponFormProp {
  className?: string;
}

export const CouponForm = ({ className }: CouponFormProp) => {
  const { getValues, setValue } = useFormContext<CheckoutFormValuesProps>();
  const {
    areCouponsEnabled,
    applyCouponCode,
    checkoutPreviewContext,
    couponPreviewMutation,
    previewMutation,
    paywall,
  } = usePaywallCheckoutContext();
  const [isInitialCouponApplied, setInitialCouponApplied] = useState(false);

  const applyCoupon = useCallback(() => {
    const formData = getValues();
    const couponCode = formData["coupon_code"];

    const couponPreviewParameters: ShowEndpointQueryParams = {
      paywall_id: paywall.id,
      coupon_code: couponCode,
      scenario: "apply_coupon",
    };

    couponPreviewMutation.mutate(couponPreviewParameters, {
      onSuccess: (data: ShowResponseEndpoint) => {
        applyCouponCode({ data });
      },
    });
  }, [getValues, applyCouponCode, setValue, couponPreviewMutation, paywall.id]);

  useEffect(() => {
    const couponCode =
      getQueryParam("coupon_code") ||
      checkoutPreviewContext?.coupon_applied?.code;

    if (couponCode && !isInitialCouponApplied && previewMutation.isSuccess) {
      setInitialCouponApplied(true);
      setValue("coupon_code", couponCode);
      applyCoupon();
    }
  }, [
    isInitialCouponApplied,
    checkoutPreviewContext?.coupon_applied?.code,
    setValue,
    applyCoupon,
    previewMutation.isSuccess,
  ]);

  if (!areCouponsEnabled) {
    return null;
  }

  if (couponPreviewMutation.isSuccess) {
    return (
      <div className="flex flex-col gap-y-3">
        <CouponBadge />
      </div>
    );
  }

  return (
    <div className={classNames("flex w-full flex-col gap-y-2", className)}>
      <CouponField applyCoupon={applyCoupon} />
    </div>
  );
};
